import Cookies from 'js-cookie'
import { apiService } from './api'

const TOKEN_KEY = 'netwall_dns_token'
const SESSION_KEY = 'netwall_dns_session'

interface LoginResponse {
  success: boolean
  data: {
    access_token: string
    session_token: string
    user: {
      id: number
      username: string
      email: string
      role: string
      full_name: string
    }
    expires_in: number
  }
}

interface User {
  id: number
  username: string
  email: string
  role: string
  full_name: string
  last_login?: string
}

class AuthService {
  private token: string | null = null
  private sessionToken: string | null = null
  private user: User | null = null

  constructor() {
    // Initialize from localStorage first (persists across sessions)
    const tokenFromStorage = localStorage.getItem('token')
    const tokenFromKey = localStorage.getItem(TOKEN_KEY)
    const tokenFromCookie = Cookies.get(TOKEN_KEY)
    
    console.log('AuthService init:', { 
      tokenFromStorage: tokenFromStorage?.substring(0, 20), 
      tokenFromKey: tokenFromKey?.substring(0, 20),
      tokenFromCookie: tokenFromCookie?.substring(0, 20)
    })
    
    this.token = tokenFromStorage || tokenFromKey || tokenFromCookie || null
    
    const userStr = localStorage.getItem('user')
    if (userStr) {
      try {
        this.user = JSON.parse(userStr)
      } catch {
        this.user = null
      }
    }
    
    console.log('AuthService initialized with token:', this.token ? 'YES' : 'NO')
  }

  async login(username: string, password: string): Promise<LoginResponse> {
    const response = await apiService.login(username, password) as LoginResponse
    
    if (response.success && response.data) {
      this.setSession(response.data)
    }
    
    return response
  }

  logout(): void {
    // Clear everything FIRST (immediate)
    this.token = null
    this.user = null
    
    // Clear cookies with same options as when set
    Cookies.remove(TOKEN_KEY, { path: '/', secure: true, sameSite: 'strict' })
    Cookies.remove(SESSION_KEY, { path: '/', secure: true, sameSite: 'strict' })
    Cookies.remove(TOKEN_KEY, { path: '' })
    Cookies.remove(SESSION_KEY, { path: '' })
    
    localStorage.removeItem('user')
    localStorage.removeItem('token')
    localStorage.removeItem(TOKEN_KEY)
    
    // Force immediate redirect (no delay)
    window.location.replace('/login')
    
    // Fire-and-forget API logout (don't wait)
    try {
      apiService.logout().catch(() => {})
    } catch (e) {
      // Ignore
    }
  }

  async verifyToken(): Promise<{ data: { valid: boolean; user: User } }> {
    return apiService.verifyToken() as Promise<{ data: { valid: boolean; user: User } }>
  }

  private setSession(data: LoginResponse['data']) {
    this.token = data.access_token
    this.sessionToken = data.session_token
    this.user = data.user
    
    console.log('setSession called with token:', data.access_token.substring(0, 20))
    
    localStorage.setItem('token', data.access_token)
    localStorage.setItem(TOKEN_KEY, data.access_token)
    Cookies.set(TOKEN_KEY, data.access_token, { expires: data.expires_in / 86400, secure: window.location.protocol === 'https:', sameSite: 'strict' })
    Cookies.set(SESSION_KEY, data.session_token, { expires: 7, secure: window.location.protocol === 'https:', sameSite: 'strict' })
    localStorage.setItem('user', JSON.stringify(data.user))
    
    console.log('setSession complete. localStorage token:', localStorage.getItem('token')?.substring(0, 20))
  }

  getToken(): string | null {
    // Always read from localStorage to get latest token
    const token = localStorage.getItem('token') || localStorage.getItem(TOKEN_KEY) || Cookies.get(TOKEN_KEY)
    if (token) {
      this.token = token
      return token
    }
    return null
  }

  getUser(): User | null {
    // Always read from localStorage to get latest user
    const userStr = localStorage.getItem('user')
    if (userStr) {
      try {
        const user = JSON.parse(userStr)
        this.user = user
        return user
      } catch {
        return null
      }
    }
    return null
  }

  isAuthenticated(): boolean {
    return !!this.getToken()
  }

  isAdmin(): boolean {
    const user = this.getUser()
    return user?.role === 'admin' || user?.role === 'superadmin'
  }

  hasRole(role: string): boolean {
    const user = this.getUser()
    if (!user) return false
    
    if (role === 'viewer') {
      return ['superadmin', 'admin', 'viewer'].includes(user.role)
    }

    if (role === 'admin') {
      return ['superadmin', 'admin'].includes(user.role)
    }
    
    return user.role === role
  }
}

export const authService = new AuthService()
export default authService
